import type { RemnantsManageRow } from '../../../api/RemnantsManage';
import type { FormulaField } from '../../../RemnantsManage/FormulaCell';

export const num = (v: number | null | undefined) =>
  v == null || Number.isNaN(v) ? '—' : v;

export const ago = (iso: string | null | undefined) => {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms)) return '—';
  const min = Math.round(ms / 60000);
  if (min < 1) return 'только что';
  if (min < 60) return `${min} мин`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} ч`;
  return `${Math.round(h / 24)} д`;
};

const until = (iso: string | null | undefined) => {
  if (!iso) return '—';
  const ms = new Date(iso).getTime() - Date.now();
  if (!Number.isFinite(ms) || ms <= 0) return '—';
  const min = Math.round(ms / 60000);
  if (min < 60) return `${min} мин`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} ч`;
  return `${Math.round(h / 24)} д`;
};

export const isQuarantineActive = (iso?: string | null) =>
  Boolean(iso && new Date(iso).getTime() > Date.now());

export const quarantineLabel = (at: string | null, reason: string | null) => {
  if (!isQuarantineActive(at)) return '—';
  return `${reason || 'карантин'} · ещё ${until(at)}`;
};

export const isWbMarketplace = (marketplace?: string | null) =>
  marketplace === 'wildberries';

export const mpLabel = (marketplace?: string | null) =>
  isWbMarketplace(marketplace) ? 'WB' : 'Ozon';

export const shortWhName = (name: string) =>
  name.replace(/^Склад\s+/i, '').trim() || name;

export type MpView = {
  share: string | null;
  shareField: FormulaField;
  blocked: boolean;
  quarantine_at: string | null;
  quarantine_reason: string | null;
  reserved: number;
  reserved_synced_at: string | null;
  last_sent: number | null;
  last_sent_at: string | null;
  mp_stock: number | null;
  mp_synced_at: string | null;
  target: number | null;
  need_update: boolean;
  formulas: NonNullable<RemnantsManageRow['wb_mp_formulas']>;
};

export const rowMpView = (
  row: RemnantsManageRow,
  marketplace: string | null | undefined,
): MpView => {
  if (isWbMarketplace(marketplace)) {
    return {
      share: row.share_wb,
      shareField: 'share_in_wildberries',
      blocked: row.wb_blocked,
      quarantine_at: row.wb_quarantine_at,
      quarantine_reason: row.wb_quarantine_reason,
      reserved: row.reserved_wb,
      reserved_synced_at: row.reserved_wb_synced_at,
      last_sent: row.wb_last_sent,
      last_sent_at: row.wb_last_sent_at,
      mp_stock: row.wb_mp_stock,
      mp_synced_at: row.wb_mp_synced_at,
      target: row.wb_target,
      need_update: row.wb_need_update,
      formulas: row.wb_mp_formulas || [],
    };
  }
  return {
    share: row.share_ozon,
    shareField: 'share_in_ozon',
    blocked: row.ozon_blocked,
    quarantine_at: row.ozon_quarantine_at,
    quarantine_reason: row.ozon_quarantine_reason,
    reserved: row.reserved_ozon,
    reserved_synced_at: row.reserved_ozon_synced_at,
    last_sent: row.ozon_last_sent,
    last_sent_at: row.ozon_last_sent_at,
    mp_stock: row.ozon_mp_stock,
    mp_synced_at: row.ozon_mp_synced_at,
    target: row.ozon_target,
    need_update: row.ozon_need_update,
    formulas: row.ozon_mp_formulas || [],
  };
};

export type BoundWarehouse = { id: number; name: string };

export const collectBoundWarehouses = (
  rows: RemnantsManageRow[],
): BoundWarehouse[] => {
  const map = new Map<number, string>();
  for (const row of rows) {
    for (const slice of row.self_warehouses_1c || []) {
      if (!map.has(slice.warehouse_id)) {
        map.set(slice.warehouse_id, slice.name);
      }
    }
  }
  return [...map.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name, 'ru'));
};
